import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';

/**
 * SwimlaneColumnCell - A droppable column cell within a swimlane row
 * Uses FLIP animation technique: tracks previous position and animates from old to new.
 */
const SwimlaneColumnCell = ({ 
  columnId, 
  swimlaneId, 
  columnIndex, 
  totalColumns, 
  activeColumnId, 
  children, 
  isCollapsed, 
  isExpanding, 
  isSwimlaneCollapsed, 
  hasCards,
  isAnyColumnDragging,
  isAnyCardDragging = false, // PERFORMANCE: Virtualize cards during card drag too
  isCardDraggingInOtherSwimlane = false, // Performance optimization: hide non-visible cards
  isBelowVisibleArea = false, // More aggressive: hide ALL cards for swimlanes below viewport
  totalCardCount,
}) => {
  const columnRef = useRef(null);
  const wrapperRef = useRef(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  
  // FLIP animation: track previous index to calculate animation offset
  const prevIndexRef = useRef(columnIndex);
  const [animationOffset, setAnimationOffset] = useState(0);
  
  // When columnIndex changes, calculate the offset and animate
  useLayoutEffect(() => {
    if (prevIndexRef.current !== columnIndex && activeColumnId) {
      // Calculate how many positions we moved
      const indexDelta = prevIndexRef.current - columnIndex;
      // Collapsed columns: 40px + 16px margin = 56px
      // Expanded columns: 320px + 16px margin = 336px
      const columnWidth = isCollapsed ? 56 : 336;
      const offset = indexDelta * columnWidth;
      
      // Set initial offset (FLIP: Invert)
      setAnimationOffset(offset);
      
      // Next frame: animate to 0 (FLIP: Play)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationOffset(0);
        });
      });
    }
    prevIndexRef.current = columnIndex;
  }, [columnIndex, activeColumnId, isCollapsed]);

  // Track hidden card count and frozen count for drag
  const [hiddenCount, setHiddenCount] = useState(0);
  const [frozenHiddenCount, setFrozenHiddenCount] = useState(null);
  
  // Freeze hidden count when ANY drag starts (column drag OR card drag)
  // This allows us to limit rendering to only visible cards during drag
  const isDraggingElsewhere = isAnyColumnDragging || isAnyCardDragging || isCardDraggingInOtherSwimlane;
  const prevIsDraggingElsewhereRef = useRef(false);
  
  useEffect(() => {
    // Only update when drag state changes (not on every render)
    if (isDraggingElsewhere && !prevIsDraggingElsewhereRef.current) {
      // Just started dragging elsewhere - freeze the current hidden count
      setFrozenHiddenCount(hiddenCount);
    } else if (!isDraggingElsewhere && prevIsDraggingElsewhereRef.current) {
      // Just stopped dragging - clear frozen count
      setFrozenHiddenCount(null);
    }
    prevIsDraggingElsewhereRef.current = isDraggingElsewhere;
  }, [isDraggingElsewhere, hiddenCount]);

  // Height preservation for swimlanes below visible area
  // When ALL cards are hidden, we need to preserve the height to prevent layout shift
  const [frozenHeight, setFrozenHeight] = useState(null);
  const prevIsBelowVisibleAreaRef = useRef(false);
  
  useEffect(() => {
    if (isBelowVisibleArea && !prevIsBelowVisibleAreaRef.current) {
      // Just became below visible area - capture current height before cards are hidden
      const el = columnRef.current;
      if (el) {
        setFrozenHeight(el.offsetHeight);
      }
    } else if (!isBelowVisibleArea && prevIsBelowVisibleAreaRef.current) {
      // No longer below visible area - clear frozen height
      setFrozenHeight(null);
    }
    prevIsBelowVisibleAreaRef.current = isBelowVisibleArea;
  }, [isBelowVisibleArea]);

  // Check if column can scroll down and count hidden cards
  useEffect(() => {
    const checkScroll = () => {
      const el = columnRef.current;
      if (el) {
        const hasMoreContent = el.scrollHeight > el.clientHeight;
        const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 10;
        setCanScrollDown(hasMoreContent && notAtBottom);
        
        // Count cards below viewport
        if (hasMoreContent && notAtBottom) {
          const cards = el.querySelectorAll('.sortable-card-wrapper');
          const containerBottom = el.getBoundingClientRect().bottom;
          let hidden = 0;
          cards.forEach(card => {
            if (card.getBoundingClientRect().top > containerBottom) {
              hidden++;
            }
          });
          setHiddenCount(hidden);
        } else {
          setHiddenCount(0);
        }
      }
    };

    checkScroll();
    const el = columnRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      // Also check on resize
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(el);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [children, isAnyColumnDragging, isAnyCardDragging]);

  // Scroll down when indicator clicked - 80% of container height or to end
  const handleScrollDown = (e) => {
    e.stopPropagation();
    const el = columnRef.current;
    if (el) {
      const scrollAmount = el.clientHeight * 0.8;
      const remainingScroll = el.scrollHeight - el.scrollTop - el.clientHeight;
      el.scrollBy({ 
        top: Math.min(scrollAmount, remainingScroll), 
        behavior: 'smooth' 
      });
    }
  };

  // Make the column cell a droppable area for cards
  // Use a unique ID combining swimlane and column to avoid conflicts
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `swimlane-drop-${swimlaneId}-${columnId}`,
    data: {
      type: 'column',
      columnId: columnId,
      swimlaneId: swimlaneId,
    },
    disabled: isSwimlaneCollapsed, // Disable drop when swimlane is collapsed
  });

  // Combine all refs
  const setRefs = (el) => {
    columnRef.current = el;
    setDroppableRef(el);
  };

  const classNames = [
    'swimlane-row__column',
    isCollapsed && 'swimlane-row__column--collapsed',
    isExpanding && 'swimlane-row__column--expanding',
    isOver && 'swimlane-row__column--over',
    isSwimlaneCollapsed && 'swimlane-row__column--swimlane-collapsed',
  ].filter(Boolean).join(' ');

  // When a column is being dragged, this cell might need to shift
  const isBeingDragged = activeColumnId === columnId;
  
  const wrapperStyle = {
    // FLIP animation: start at offset, transition to 0
    transform: animationOffset !== 0 ? `translateX(${animationOffset}px)` : undefined,
    // Animate the transform
    transition: animationOffset === 0 && activeColumnId ? 'transform 200ms ease' : 'none',
  };

  // Calculate how many cards to render during drag (column drag OR card drag in other swimlane)
  // Use frozen hidden count to determine visible count (visible = total - hidden)
  const visibleCardCount = isDraggingElsewhere && frozenHiddenCount !== null
    ? Math.max(3, totalCardCount - frozenHiddenCount)
    : totalCardCount;
  
  // Display hidden count - frozen during drag, dynamic otherwise
  const displayHiddenCount = isDraggingElsewhere && frozenHiddenCount !== null ? frozenHiddenCount : hiddenCount;

  // During drag (column or card in other swimlane), filter children to only render visible cards
  const filteredChildren = React.Children.map(children, (child, index) => {
    // If not dragging elsewhere, render all children
    if (!isDraggingElsewhere) return child;
    
    // If this is a SortableContext, we need to filter its children
    if (child?.type?.name === 'SortableContext' || child?.props?.items) {
      // Clone the SortableContext with filtered children
      const sortableChildren = React.Children.toArray(child.props.children);
      const limitedChildren = sortableChildren.slice(0, visibleCardCount);
      return React.cloneElement(child, {}, limitedChildren);
    }
    
    return child;
  });

  const wrapperClasses = [
    'swimlane-row__column-wrapper',
    isBeingDragged && 'swimlane-row__column-wrapper--dragging',
  ].filter(Boolean).join(' ');

  return (
    <div ref={wrapperRef} className={wrapperClasses} style={wrapperStyle}>
      {/* Placeholder shown when this column is being dragged */}
      {isBeingDragged && (
        <div className="swimlane-row__column-placeholder" />
      )}
      <div 
        ref={setRefs}
        className={classNames}
        data-column-id={columnId}
        data-visible-count={visibleCardCount}
        style={{ 
          visibility: isBeingDragged ? 'hidden' : 'visible',
          // Preserve height when ALL cards are hidden (swimlane below visible area)
          minHeight: frozenHeight !== null ? `${frozenHeight}px` : undefined,
        }}
      >
        {filteredChildren}
      </div>
      {/* Scroll indicator - show during drag with frozen count, otherwise show dynamic */}
      {/* Hide for swimlanes below visible area (all cards hidden there) */}
      {!isBelowVisibleArea && ((isDraggingElsewhere && frozenHiddenCount > 0) || (!isDraggingElsewhere && canScrollDown)) && hasCards && !isCollapsed && !isSwimlaneCollapsed && (
        <div 
          className="swimlane-row__scroll-indicator"
          onClick={isDraggingElsewhere ? undefined : handleScrollDown}
          title="Scroll to see more"
          style={{ pointerEvents: isDraggingElsewhere ? 'none' : 'auto' }}
        >
          <span className="swimlane-row__scroll-indicator-btn">
            {displayHiddenCount > 0 ? `+${displayHiddenCount} more` : 'More'}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
      )}
    </div>
  );
};

export default SwimlaneColumnCell;

